/**
 * DesktopChatWidget - Widget Chat flottante per Desktop
 *
 * Pulsante flottante in basso a destra che apre la chat.
 * Include badge notifiche non lette e notifiche browser/sonore.
 *
 * CONFIGURAZIONE:
 * - autoOpenOnMessage: se true, apre automaticamente la chat quando arriva un nuovo messaggio
 */

import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, ArrowLeft, Users, Search, Monitor, Smartphone, Circle, Volume2, VolumeX, Bell, BellOff, Trash2, Camera, Check, CheckCheck, XCircle, Megaphone, Reply } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import useDirectMessages, { type DMUser, type DMConversation } from '../../hooks/useDirectMessages';
import { playFullNotification, resumeAudioContext } from '../../utils/audioNotifications';
import BroadcastModal from './BroadcastModal';

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURAZIONE CHAT - Modifica questi valori per personalizzare comportamento
// ═══════════════════════════════════════════════════════════════════════════
const CHAT_CONFIG = {
  // Se true, apre automaticamente la chat quando arriva un nuovo messaggio
  autoOpenOnMessage: true,

  // Durata animazione pulse sul pulsante (ms)
  pulseAnimationDuration: 3000,
};
// ═══════════════════════════════════════════════════════════════════════════

export default function DesktopChatWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const isMobileOfficina = location.pathname.startsWith('/mobile-officina');
  const [isOpen, setIsOpen] = useState(false);
  const [currentView, setCurrentView] = useState<'conversations' | 'users' | 'chat'>('conversations');
  const [messageText, setMessageText] = useState('');
  // Allineamento stile WhatsApp col ChatPanel mobile (2026-07-23): citazione
  const [citazione, setCitazione] = useState<{ nome: string; testo: string } | null>(null);

  const etichettaGiorno = (v?: string | number) => {
    if (!v) return '';
    const d = new Date(v);
    const oggi = new Date();
    const ieri = new Date(Date.now() - 86400000);
    const stesso = (a: Date, b: Date) => a.toDateString() === b.toDateString();
    if (stesso(d, oggi)) return 'Oggi';
    if (stesso(d, ieri)) return 'Ieri';
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  const spezzaCitazione = (testo: string): { quote: { nome: string; testo: string } | null; corpo: string } => {
    const m = /^«([^:»]{1,60}): ([^»]{1,160})»\n([\s\S]*)$/.exec(testo || '');
    if (!m) return { quote: null, corpo: testo };
    return { quote: { nome: m[1], testo: m[2] }, corpo: m[3] };
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [autoOpenEnabled, setAutoOpenEnabled] = useState(CHAT_CONFIG.autoOpenOnMessage);
  const [isPulsing, setIsPulsing] = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const canBroadcast = user?.role === 'admin' || user?.role === 'superadmin';
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastMessageIdRef = useRef<number>(0);

  const isMobileOperator = location.pathname.startsWith('/mobile-operator');
  const isMobileContext = isMobileOfficina || isMobileOperator;

  // Su PWA mobile l'auth desktop è null → usa localStorage popolato dagli auth mobile hook (useMobileOfficinaAuth / useMobileOperatorsAuth)
  const [mobileUserId, setMobileUserId] = useState<string>(() => isMobileContext ? (localStorage.getItem('userId') || '') : '');
  const [mobileUserName, setMobileUserName] = useState<string>(() => isMobileContext ? (localStorage.getItem('userName') || '') : '');

  useEffect(() => {
    if (!isMobileContext) return;
    // Poll localStorage finché l'auth mobile non popola userId (l'hook setta al mount dopo auth check)
    const interval = setInterval(() => {
      const id = localStorage.getItem('userId') || '';
      const name = localStorage.getItem('userName') || '';
      if (id !== mobileUserId) setMobileUserId(id);
      if (name !== mobileUserName) setMobileUserName(name);
    }, 1000);
    return () => clearInterval(interval);
  }, [isMobileContext, mobileUserId, mobileUserName]);

  const userId = user?.id ? String(user.id) : mobileUserId;
  const userName = user?.name || mobileUserName || 'Desktop User';
  const effectiveUserType: 'desktop' | 'mobile' = isMobileContext ? 'mobile' : 'desktop';
  const pulseTimeoutRef = useRef<number | null>(null);

  const {
    isConnected,
    isConnecting,
    conversations,
    messages,
    allUsers,
    totalUnreadCount,
    lastReceivedMessage,
    activeChat,
    typingUsers,
    sendMessage,
    setActiveChat,
    sendTypingIndicator,
    loadConversations,
    loadAllUsers,
    clearMessages
  } = useDirectMessages({
    userId,
    userName,
    userType: effectiveUserType,
    autoConnect: !!userId
  });

  // Inizializza audio context al primo click/touch
  useEffect(() => {
    const unlockAudio = () => {
      resumeAudioContext();
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
    };

    document.addEventListener('click', unlockAudio, { once: true });
    document.addEventListener('touchstart', unlockAudio, { once: true });

    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
    };
  }, []);

  // Richiedi permesso notifiche
  useEffect(() => {
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        setNotificationsEnabled(true);
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(permission => {
          setNotificationsEnabled(permission === 'granted');
        });
      }
    }
  }, []);

  // Scroll to bottom quando cambiano i messaggi
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input quando si apre la chat
  useEffect(() => {
    if (currentView === 'chat' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [currentView, activeChat]);

  // Carica dati quando si apre il widget
  useEffect(() => {
    if (isOpen && userId) {
      loadAllUsers();
      loadConversations();
    }
  }, [isOpen, userId, loadAllUsers, loadConversations]);

  // Notifica per nuovi messaggi
  useEffect(() => {
    if (!lastReceivedMessage) return;

    const messageId = lastReceivedMessage.id || lastReceivedMessage.messageId || 0;

    // DEBUG: Log per tracciare il flusso
    console.log('[Chat Desktop] Check message:', {
      messageId,
      lastMessageIdRef: lastMessageIdRef.current,
      fromUserId: lastReceivedMessage.fromUserId,
      userId,
      isNew: messageId > lastMessageIdRef.current,
      isFromOther: String(lastReceivedMessage.fromUserId) !== String(userId),
      fromUserIdType: typeof lastReceivedMessage.fromUserId,
      userIdType: typeof userId
    });

    // Se è un nuovo messaggio (non nostro) - confronto come stringhe
    if (messageId > lastMessageIdRef.current && String(lastReceivedMessage.fromUserId) !== String(userId)) {
      lastMessageIdRef.current = messageId;
      console.log('[Chat Desktop] ✅ Nuova notifica da:', lastReceivedMessage.fromUserName);

      // Suono multi-tono SEMPRE (anche se chat aperta - ambiente officina rumoroso)
      if (soundEnabled) {
        playFullNotification('chat').catch(err => {
          console.log('[Chat Desktop] Audio notification error:', err);
        });
      }

      // Altre azioni solo se il widget è chiuso o non stiamo guardando quella chat
      if (!isOpen || activeChat?.userId !== lastReceivedMessage.fromUserId) {
        // Notifica browser
        if (notificationsEnabled && !document.hasFocus()) {
          new Notification(`💬 ${lastReceivedMessage.fromUserName}`, {
            body: lastReceivedMessage.text || lastReceivedMessage.message || 'Nuovo messaggio',
            icon: '/assets/logos/coronatek-sticky-192x192.png',
            tag: `dm-${lastReceivedMessage.fromUserId}`
          });
        }

        // Animazione pulse sul pulsante
        setIsPulsing(true);
        if (pulseTimeoutRef.current) {
          clearTimeout(pulseTimeoutRef.current);
        }
        pulseTimeoutRef.current = window.setTimeout(() => {
          setIsPulsing(false);
        }, CHAT_CONFIG.pulseAnimationDuration);

        // Auto-apertura chat se abilitata
        if (autoOpenEnabled && !isOpen) {
          setIsOpen(true);
          // Apri direttamente la chat con il mittente
          setActiveChat({
            userId: lastReceivedMessage.fromUserId,
            userName: lastReceivedMessage.fromUserName,
            userType: lastReceivedMessage.fromUserType || 'desktop',
            status: 'online'
          });
          setCurrentView('chat');
        }
      }
    }
  }, [lastReceivedMessage, isOpen, activeChat, userId, soundEnabled, notificationsEnabled, autoOpenEnabled, setActiveChat]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (pulseTimeoutRef.current) {
        clearTimeout(pulseTimeoutRef.current);
      }
    };
  }, []);

  // Filtro utenti per ricerca
  const filteredUsers = allUsers.filter(u => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return u.userName.toLowerCase().includes(query) ||
           u.email?.toLowerCase().includes(query);
  });

  // Invia messaggio
  const handleSendMessage = async () => {
    if (!messageText.trim() || !activeChat) return;

    const text = (citazione ? `«${citazione.nome}: ${citazione.testo.slice(0, 160).replace(/»/g, "'")}»\n` : '') + messageText.trim();
    setCitazione(null);
    setMessageText('');

    try {
      await sendMessage(activeChat, text);
    } catch (err) {
      console.error('Errore invio messaggio:', err);
      setMessageText(text); // Ripristina in caso di errore
    }
  };

  // Apri chat con utente
  const openChatWith = (user: DMUser) => {
    setActiveChat(user);
    setCurrentView('chat');
  };

  // Torna alla lista
  const goBack = () => {
    setActiveChat(null);
    setCurrentView('conversations');
  };

  // Format timestamp
  const formatTime = (timestamp?: string) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  };

  // Parse message preview - handles JSON strings and extracts text
  const parsePreview = (preview?: string): string => {
    if (!preview) return 'Nessun messaggio';

    // Se inizia con { potrebbe essere JSON (anche troncato)
    if (preview.trim().startsWith('{')) {
      // Prima prova JSON.parse per JSON completo
      try {
        const parsed = JSON.parse(preview);
        if (parsed.text) {
          return parsed.text.replace(/\\n/g, ' ').replace(/\n/g, ' ');
        }
        if (parsed.message) {
          return parsed.message.replace(/\\n/g, ' ').replace(/\n/g, ' ');
        }
      } catch {
        // JSON incompleto/troncato - usa regex per estrarre il contenuto
        // Cerca pattern {"text":" o {"message":"
        const textMatch = preview.match(/"text"\s*:\s*"([^"]*)/) ||
                         preview.match(/"message"\s*:\s*"([^"]*)/);
        if (textMatch && textMatch[1]) {
          return textMatch[1].replace(/\\n/g, ' ').replace(/\n/g, ' ');
        }
      }
    }

    // Rimuovi eventuali newline per la preview
    return preview.replace(/\\n/g, ' ').replace(/\n/g, ' ');
  };

  if (!userId && !isMobileContext) return null;

  return (
    <>
      {/* Pulsante flottante — su mobile nascosto quando chat aperta (chiusura via X nell'header) */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 w-14 h-14 flex items-center justify-center transition-all hover:scale-105 z-[9999] ${
          isPulsing ? 'animate-pulse' : ''
        }`}
        style={isOpen ? { display: 'none' } : undefined}
      >
        {isOpen ? (
          <div className="w-14 h-14 rounded-full bg-indigo-600 flex items-center justify-center shadow-lg">
            <X className="w-6 h-6 text-white" />
          </div>
        ) : (
          <>
            {isMobileOfficina ? (
              <img src="/assets/mobile_officina/UI/chat.webp" alt="Chat" className="w-full h-full object-contain drop-shadow-lg" draggable={false} />
            ) : (
              <div className="w-14 h-14 rounded-full bg-indigo-600 flex items-center justify-center shadow-lg">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
            )}
            {totalUnreadCount > 0 && (
              <span className={`absolute -top-1 -right-1 text-white text-xs font-bold min-w-[20px] h-5 flex items-center justify-center rounded-full px-1 ${
                isPulsing ? 'bg-white text-green-600' : 'bg-red-500'
              }`}>
                {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
              </span>
            )}
          </>
        )}
      </button>

      {/* Pannello chat - z-index alto per essere sopra i modal.
          Su mobile-officina: fullscreen (evita sbordo su schermi stretti).
          Su desktop: finestra flottante bottom-right. */}
      {isOpen && (
        <div
          className={isMobileContext
            ? "fixed inset-0 z-[9998] bg-gray-900 flex flex-col overflow-hidden"
            : "fixed bottom-24 right-6 z-[9998] w-96 h-[500px] bg-gray-900 rounded-xl shadow-2xl border border-gray-700 flex flex-col overflow-hidden"}
          style={isMobileContext ? {
            height: '100dvh',
            paddingTop: 'env(safe-area-inset-top, 0)',
            paddingBottom: 'env(safe-area-inset-bottom, 0)',
            paddingLeft: 'env(safe-area-inset-left, 0)',
            paddingRight: 'env(safe-area-inset-right, 0)'
          } : undefined}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {currentView === 'chat' && activeChat ? (
                <>
                  <button onClick={goBack} className="text-white/80 hover:text-white">
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="flex-1">
                    <div className="font-semibold text-white flex items-center gap-2">
                      {activeChat.userName}
                      {activeChat.userType === 'mobile' ? (
                        <Smartphone className="w-3 h-3 text-indigo-200" />
                      ) : (
                        <Monitor className="w-3 h-3 text-indigo-200" />
                      )}
                    </div>
                    {typingUsers.has(`${activeChat.userType}-${activeChat.userId}`) && (
                      <div className="text-xs text-indigo-200 animate-pulse">Sta scrivendo...</div>
                    )}
                  </div>
                  {/* Pulsante auto-apertura (campana) + pulisci chat (cestino) */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setAutoOpenEnabled(!autoOpenEnabled)}
                      className={`p-1.5 hover:bg-white/10 rounded-full transition-colors ${autoOpenEnabled ? 'text-green-400' : 'text-white/40'}`}
                      title={autoOpenEnabled ? 'Auto-apertura attiva' : 'Auto-apertura disattivata'}
                    >
                      {autoOpenEnabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                    </button>
                    {messages.length > 0 && (
                      <button
                        onClick={() => setShowClearConfirm(true)}
                        className="p-1.5 hover:bg-white/10 rounded-full transition-colors"
                        title="Pulisci messaggi"
                      >
                        <Trash2 className="w-4 h-4 text-red-300 hover:text-red-200" />
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-white" />
                  <span className="font-semibold text-white">Chat</span>
                  {totalUnreadCount > 0 && (
                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {totalUnreadCount}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Broadcast (solo admin/superadmin) */}
              {canBroadcast && (
                <button
                  onClick={() => setShowBroadcastModal(true)}
                  className="p-1 text-purple-300 hover:text-purple-200"
                  title="Invia annuncio push a gruppo di utenti"
                >
                  <Megaphone className="w-4 h-4" />
                </button>
              )}

              {/* Toggle suono */}
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className={`p-1 ${soundEnabled ? 'text-white/70 hover:text-white' : 'text-white/40 hover:text-white/60'}`}
                title={soundEnabled ? 'Disattiva suoni' : 'Attiva suoni'}
              >
                {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>

              {/* Status connessione */}
              <div className="flex items-center gap-1" title={isConnected ? 'Connesso' : 'Disconnesso'}>
                <Circle className={`w-2 h-2 ${isConnected ? 'fill-green-400 text-green-400' : 'fill-yellow-400 text-yellow-400'}`} />
              </div>

              {/* Pulsante chiudi chat */}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                title="Chiudi chat"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Tabs (solo se non in chat) */}
          {currentView !== 'chat' && (
            <div className="flex border-b border-gray-700">
              <button
                onClick={() => setCurrentView('conversations')}
                className={`flex-1 py-2 text-sm font-medium ${
                  currentView === 'conversations'
                    ? 'text-indigo-400 border-b-2 border-indigo-400'
                    : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                <MessageCircle className="w-4 h-4 inline-block mr-1" />
                Chat
              </button>
              <button
                onClick={() => setCurrentView('users')}
                className={`flex-1 py-2 text-sm font-medium ${
                  currentView === 'users'
                    ? 'text-indigo-400 border-b-2 border-indigo-400'
                    : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                <Users className="w-4 h-4 inline-block mr-1" />
                Utenti
              </button>
            </div>
          )}

          {/* Status connessione */}
          {!isConnected && !isConnecting && (
            <div className="bg-yellow-900/30 border-b border-yellow-700/30 text-yellow-300/80 px-3 py-1.5 text-xs flex items-center gap-2">
              <Circle className="w-2 h-2 fill-yellow-400" />
              Modalità offline - I messaggi verranno inviati via API
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Lista conversazioni */}
            {currentView === 'conversations' && (
              <div className="divide-y divide-gray-700">
                {conversations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                    <MessageCircle className="w-12 h-12 text-gray-600 mb-3" />
                    <p className="text-gray-400 mb-2">Nessuna conversazione</p>
                    <button
                      onClick={() => setCurrentView('users')}
                      className="text-indigo-400 text-sm hover:underline"
                    >
                      Inizia una nuova chat
                    </button>
                  </div>
                ) : (
                  conversations.map((conv: DMConversation) => (
                    <button
                      key={conv.id}
                      onClick={() => openChatWith({
                        userId: conv.other_user_id,
                        userName: conv.other_user_name,
                        userType: conv.other_user_type,
                        status: conv.other_user_status
                      })}
                      className="w-full p-3 hover:bg-gray-800 flex items-center gap-3 text-left"
                    >
                      <div className="relative">
                        <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-semibold">
                          {conv.other_user_name.charAt(0).toUpperCase()}
                        </div>
                        {conv.other_user_status === 'online' && (
                          <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-gray-900" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-white truncate flex items-center gap-1">
                            {conv.other_user_name}
                            {conv.other_user_type === 'mobile' ? (
                              <Smartphone className="w-3 h-3 text-gray-400" />
                            ) : (
                              <Monitor className="w-3 h-3 text-gray-400" />
                            )}
                          </span>
                          {conv.last_message_at && (
                            <span className="text-xs text-gray-500">
                              {formatTime(conv.last_message_at)}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-400 truncate">
                          {parsePreview(conv.last_message_preview)}
                        </p>
                      </div>
                      {conv.unread_count > 0 && (
                        <span className="bg-indigo-600 text-white text-xs px-2 py-0.5 rounded-full">
                          {conv.unread_count}
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>
            )}

            {/* Lista utenti */}
            {currentView === 'users' && (
              <div>
                {/* Search */}
                <div className="p-3 border-b border-gray-700">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                      type="text"
                      placeholder="Cerca utente..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full bg-gray-800 border border-gray-600 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                    />
                  </div>
                </div>

                <div className="divide-y divide-gray-700">
                  {filteredUsers.length === 0 ? (
                    <div className="py-8 text-center text-gray-500">
                      {searchQuery ? 'Nessun utente trovato' : 'Nessun utente disponibile'}
                    </div>
                  ) : (
                    filteredUsers.map((u) => {
                      // Cerca last_seen dalla conversazione corrispondente
                      const conv = conversations.find(c => c.other_user_id === u.userId && c.other_user_type === u.userType);
                      const lastSeenText = (() => {
                        const lastSeen = conv?.other_user_last_seen;
                        if (!lastSeen || u.online) return null;
                        const diff = Date.now() - new Date(lastSeen).getTime();
                        if (diff < 0 || isNaN(diff)) return null;
                        const minutes = Math.floor(diff / 60000);
                        if (minutes < 1) return 'ora';
                        if (minutes < 60) return `${minutes}m fa`;
                        const hours = Math.floor(minutes / 60);
                        if (hours < 24) return `${hours}h fa`;
                        return new Date(lastSeen).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
                      })();
                      return (
                      <button
                        key={`${u.userType}-${u.userId}`}
                        onClick={() => openChatWith(u)}
                        className="w-full p-3 hover:bg-gray-800 flex items-center gap-3 text-left"
                      >
                        <div className="relative">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                            u.userType === 'mobile' ? 'bg-green-600' : 'bg-indigo-600'
                          }`}>
                            {u.userName.charAt(0).toUpperCase()}
                          </div>
                          {/* Pallino stato: verde=online, giallo=mobile away, grigio=offline */}
                          <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-gray-900 ${
                            u.online
                              ? (u.status === 'away' ? 'bg-amber-500' : 'bg-green-500')
                              : 'bg-gray-500'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-white flex items-center gap-2">
                            <span className="truncate">{u.userName}</span>
                            {u.userType === 'mobile' ? (
                              <span className="text-xs text-green-400 flex items-center gap-1 flex-shrink-0">
                                <Smartphone className="w-3 h-3" /> Mobile
                              </span>
                            ) : (
                              <span className="text-xs text-indigo-400 flex items-center gap-1 flex-shrink-0">
                                <Monitor className="w-3 h-3" /> Desktop
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 text-xs">
                            <span className={u.online ? 'text-green-400' : 'text-gray-500'}>
                              {u.online ? 'Online' : 'Offline'}
                            </span>
                            {!u.online && lastSeenText && (
                              <span className="text-gray-600">- {lastSeenText}</span>
                            )}
                          </div>
                        </div>
                      </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* Chat view */}
            {currentView === 'chat' && activeChat && (
              <div className="flex flex-col h-full">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-3 pt-2 pb-1 space-y-2">
                  {messages.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      Inizia la conversazione con {activeChat.userName}
                    </div>
                  ) : (
                    messages.map((msg, idx) => {
                      const isOwnMessage = msg.fromUserId === userId;
                      const isPhotoRequest = msg.messageType === 'photo_request' || msg.isPhotoRequest;
                      const photoData = msg.photoRequestData;
                      // Fix: usa photoRequestData.status invece del campo inesistente msg.requestStatus
                      const requestStatus = (msg as any).requestStatus || photoData?.status;

                      // Rendering speciale per richieste foto
                      // Le mostriamo sempre a destra per il desktop (sono sempre "inviate" dal desktop)
                      // anche se nel DB gli ID potrebbero essere invertiti per bug storici
                      if (isPhotoRequest) {
                        return (
                          <div
                            key={msg.id || msg.messageId || idx}
                            className="flex justify-end"
                          >
                            <div className="max-w-[90%]">
                              <div className="bg-gradient-to-br from-purple-600/90 to-indigo-600/90 rounded-2xl rounded-br-md p-3 border border-purple-500/30">
                                <div className="flex items-center gap-2 mb-2">
                                  <Camera className="w-4 h-4 text-white" />
                                  <span className="text-white font-semibold text-sm">Richiesta Foto Inviata</span>
                                </div>
                                <p className="text-white/90 text-sm">
                                  {photoData?.title || 'Richiesta foto'}
                                </p>
                                {photoData?.description && (
                                  <p className="text-white/70 text-xs mt-1">
                                    {photoData.description}
                                  </p>
                                )}

                                {/* Stato richiesta */}
                                {requestStatus === 'completed' && (
                                  <div className="flex items-center gap-1 mt-2 text-blue-300 text-xs">
                                    <Check className="w-3 h-3" />
                                    <span>✅ Completata</span>
                                  </div>
                                )}
                                {requestStatus === 'accepted' && (
                                  <div className="flex items-center gap-1 mt-2 text-green-300 text-xs">
                                    <Check className="w-3 h-3" />
                                    <span>Accettata - in corso...</span>
                                  </div>
                                )}
                                {requestStatus === 'refused' && (
                                  <div className="flex items-center gap-1 mt-2 text-red-300 text-xs">
                                    <XCircle className="w-3 h-3" />
                                    <span>Rifiutata</span>
                                  </div>
                                )}
                                {(!requestStatus || requestStatus === 'pending') && (
                                  <div className="flex items-center gap-1 mt-2 text-yellow-300 text-xs">
                                    <Circle className="w-3 h-3 animate-pulse" />
                                    <span>In attesa di risposta...</span>
                                  </div>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mt-1 text-right mr-1">
                                {formatTime(msg.timestamp || msg.created_at)}
                              </p>
                            </div>
                          </div>
                        );
                      }

                      // Rendering normale — allineato allo stile WhatsApp del
                      // ChatPanel mobile (2026-07-23): separatori giorno, bolle
                      // con coda, ora+spunte in bolla, citazioni, cita al hover
                      const dataMsg = msg.timestamp || msg.created_at;
                      const dataPrec = idx > 0 ? (messages[idx - 1].timestamp || messages[idx - 1].created_at) : null;
                      const nuovoGiorno = !dataPrec || new Date(dataMsg).toDateString() !== new Date(dataPrec).toDateString();
                      const ultimoDelGruppo = idx === messages.length - 1 || messages[idx + 1].fromUserId !== msg.fromUserId;
                      const { quote, corpo } = spezzaCitazione(msg.text || msg.message || '');
                      const coloreBolla = isOwnMessage ? 'bg-emerald-700' : 'bg-gray-700';
                      return (
                        <div key={msg.id || msg.messageId || idx}>
                          {nuovoGiorno && (
                            <div className="flex justify-center my-2">
                              <span className="px-3 py-1 rounded-full bg-gray-800/90 border border-gray-700 text-[11px] text-gray-400">
                                {etichettaGiorno(dataMsg)}
                              </span>
                            </div>
                          )}
                          <div className={`group flex items-center gap-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                            {isOwnMessage && (
                              <button
                                onClick={() => setCitazione({ nome: 'Tu', testo: corpo.slice(0, 160) })}
                                className="opacity-0 group-hover:opacity-100 p-1 rounded text-gray-500 hover:text-gray-300 transition-opacity"
                                title="Rispondi citando"
                              >
                                <Reply className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <div className={`relative max-w-[80%] rounded-2xl px-3 py-1.5 text-white ${coloreBolla} ${
                              ultimoDelGruppo ? (isOwnMessage ? 'rounded-br-none' : 'rounded-bl-none') : ''
                            }`}>
                              {ultimoDelGruppo && (
                                <span
                                  className={`absolute bottom-0 w-2.5 h-2.5 ${coloreBolla} ${isOwnMessage ? '-right-1' : '-left-1'}`}
                                  style={{ clipPath: isOwnMessage ? 'polygon(0 0, 100% 100%, 0 100%)' : 'polygon(100% 0, 100% 100%, 0 100%)' }}
                                />
                              )}
                              {quote && (
                                <div className={`mb-1 mt-0.5 px-2 py-1 rounded-lg border-l-2 ${isOwnMessage ? 'bg-black/20 border-emerald-300' : 'bg-black/25 border-blue-400'}`}>
                                  <p className="text-[11px] font-semibold opacity-90">{quote.nome}</p>
                                  <p className="text-[11px] opacity-75 truncate">{quote.testo}</p>
                                </div>
                              )}
                              <p className="text-sm break-words whitespace-pre-wrap pr-1">
                                {corpo}
                                <span className={`inline-block ${isOwnMessage ? 'w-14' : 'w-9'}`} />
                              </p>
                              <span className="absolute bottom-1 right-2 flex items-center gap-0.5 text-[10px] text-white/70">
                                {formatTime(dataMsg)}
                                {isOwnMessage && (msg.read_at
                                  ? <CheckCheck className="w-3.5 h-3.5 text-sky-300" />
                                  : <CheckCheck className="w-3.5 h-3.5 text-white/60" />)}
                              </span>
                            </div>
                            {!isOwnMessage && (
                              <button
                                onClick={() => setCitazione({ nome: msg.fromUserName || 'Utente', testo: corpo.slice(0, 160) })}
                                className="opacity-0 group-hover:opacity-100 p-1 rounded text-gray-500 hover:text-gray-300 transition-opacity"
                                title="Rispondi citando"
                              >
                                <Reply className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input messaggio */}
                <div className="px-3 py-2 border-t border-gray-700 flex-shrink-0">
                  {citazione && (
                    <div className="mb-2 flex items-start gap-2 px-3 py-1.5 rounded-lg bg-gray-700/70 border-l-2 border-emerald-400">
                      <Reply className="w-4 h-4 text-emerald-300 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-semibold text-emerald-300">{citazione.nome}</p>
                        <p className="text-[11px] text-gray-300 truncate">{citazione.testo}</p>
                      </div>
                      <button onClick={() => setCitazione(null)} className="p-1" aria-label="Annulla citazione">
                        <X className="w-3.5 h-3.5 text-gray-400" />
                      </button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      ref={inputRef}
                      type="text"
                      placeholder="Scrivi un messaggio..."
                      value={messageText}
                      onChange={(e) => {
                        setMessageText(e.target.value);
                        sendTypingIndicator(activeChat, e.target.value.length > 0);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      className="flex-1 bg-gray-800 border border-gray-600 rounded-lg px-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={!messageText.trim()}
                      className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modale conferma pulisci messaggi */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-white font-semibold text-lg mb-2">Pulisci messaggi</h3>
            <p className="text-gray-400 text-sm mb-4">
              Vuoi cancellare tutti i messaggi dalla vista? I messaggi rimarranno salvati sul server.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="flex-1 py-2 px-4 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={() => {
                  clearMessages();
                  setShowClearConfirm(false);
                }}
                className="flex-1 py-2 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Pulisci
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Broadcast annuncio (solo admin/superadmin) */}
      {canBroadcast && (
        <BroadcastModal
          isOpen={showBroadcastModal}
          onClose={() => setShowBroadcastModal(false)}
        />
      )}
    </>
  );
}
